/**
 * dcoded-llp cart notification — stays open until X / Escape / outside click / View cart.
 * Open state uses data-open + inline styles (not only .active) so ATC / cart-bubble
 * animations and morph cannot hide it.
 */
class DcodedLlpCartNotification extends HTMLElement {
  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {MutationObserver | null} */
  #openGuard = null;

  /** @type {number | null} */
  #heartbeat = null;

  /** @type {boolean} */
  #asserting = false;

  /** @type {boolean} */
  #hasProductRow = false;

  /** @type {Record<string, any> | null} */
  #paintedItem = null;

  /** Wait for painted product before opening (avoids flashing previous ATC item). */
  /** @type {boolean} */
  #pendingAtcOpen = false;

  /** @type {HTMLElement | null} */
  scrim = null;

  /** @type {boolean} */
  #shouldStayOpen = false;

  /** @type {number} */
  #ignoreOutsideUntil = 0;

  /** @type {number} */
  #ignoreReopenUntil = 0;

  constructor() {
    super();
    this.closeTimeout = null;
    this.notification = null;
    this.scrim = null;
    this.cartTrigger = null;
    this.onCartUpdate = this.handleCartUpdate.bind(this);
    this.onAtcStart = this.handleAtcStart.bind(this);
    this.onViewportChange = this.updatePosition.bind(this);
    this.onOutsidePointerDown = this.#handleOutsidePointerDown.bind(this);
  }

  connectedCallback() {
    if (window.__dcodedLlpNotificationHost && window.__dcodedLlpNotificationHost !== this) {
      // Duplicate host (e.g. header remorph) — steal control, keep live toast
      window.__dcodedLlpNotificationHost = this;
    } else {
      window.__dcodedLlpNotificationHost = this;
    }

    // Parser may fire connectedCallback before children exist
    queueMicrotask(() => this.#bootstrap());
  }

  #bootstrap() {
    if (window.__dcodedLlpNotificationHost !== this) return;

    this.#mountPanel();
    this.#bindPanelUi();

    document.addEventListener('cart:update', this.onCartUpdate);
    document.addEventListener('dcoded-llp:atc-start', this.onAtcStart);
    document.addEventListener('pointerdown', this.onOutsidePointerDown, true);
    window.addEventListener('resize', this.onViewportChange);
    window.addEventListener('scroll', this.onViewportChange, true);
    this.#suppressForeignNotifications();
    this.cacheCartTrigger();
    this.updatePosition();

    if (window.__dcodedLlpNotificationShouldStayOpen) {
      this.#shouldStayOpen = true;
      this.open();
    }
  }

  disconnectedCallback() {
    if (window.__dcodedLlpNotificationHost === this) {
      window.__dcodedLlpNotificationHost = null;
    }
    document.removeEventListener('cart:update', this.onCartUpdate);
    document.removeEventListener('dcoded-llp:atc-start', this.onAtcStart);
    document.removeEventListener('pointerdown', this.onOutsidePointerDown, true);
    window.removeEventListener('resize', this.onViewportChange);
    window.removeEventListener('scroll', this.onViewportChange, true);
    this.#observer?.disconnect();
    // Keep openGuard + heartbeat on the live body panel even if host remounts
  }

  #mountPanel() {
    const live =
      document.querySelector('#dcoded-llp-cart-notification[data-dcoded-llp-live="true"]') ||
      document.querySelector('#dcoded-llp-cart-notification[data-open="true"]');
    const local = this.querySelector('#dcoded-llp-cart-notification');
    let panel =
      live ||
      document.getElementById('dcoded-llp-cart-notification') ||
      local;

    if (!panel) {
      this.notification = null;
      return;
    }

    // Remove duplicate inactive copies (header remorph creates these)
    document.querySelectorAll('#dcoded-llp-cart-notification').forEach((el) => {
      if (el !== panel) el.remove();
    });

    panel.setAttribute('data-dcoded-llp-live', 'true');
    if (panel.parentElement !== document.body) {
      document.body.appendChild(panel);
    }

    this.notification = panel;
    this.#mountScrim();
  }

  #mountScrim() {
    const live = document.getElementById('dcoded-llp-cart-notification-scrim');
    const local = this.querySelector('#dcoded-llp-cart-notification-scrim');
    const scrim = live || local;
    if (!(scrim instanceof HTMLElement)) {
      this.scrim = null;
      return;
    }

    document.querySelectorAll('#dcoded-llp-cart-notification-scrim').forEach((el) => {
      if (el !== scrim) el.remove();
    });

    if (scrim.parentElement !== document.body) {
      document.body.appendChild(scrim);
    }

    this.scrim = scrim;
  }

  #bindPanelUi() {
    this.#bindScrimUi();
    if (!this.notification || this.notification.dataset.dcodedLlpBound === 'true') return;
    this.notification.dataset.dcodedLlpBound = 'true';

    this.notification.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close('escape');
    });
    this.notification.querySelectorAll('[data-dcoded-llp-notification-close]').forEach((closeButton) => {
      closeButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.close('button');
      });
    });
    const viewCartButton = this.notification.querySelector('#dcoded-llp-cart-notification-button');
    if (viewCartButton) {
      viewCartButton.addEventListener('click', this.handleViewCartClick.bind(this));
    }
  }

  #bindScrimUi() {
    if (!(this.scrim instanceof HTMLElement)) this.#mountScrim();
    if (!(this.scrim instanceof HTMLElement) || this.scrim.dataset.dcodedLlpBound === 'true') return;
    this.scrim.dataset.dcodedLlpBound = 'true';
    this.scrim.addEventListener('pointerdown', (event) => {
      this.#dismissFromOutside(event);
    });
    this.scrim.addEventListener('click', (event) => {
      this.#dismissFromOutside(event);
    });
  }

  #closeOnOutsideEnabled() {
    if (this.dataset.closeOnOutside === 'false') return false;
    if (window.Theme?.cartNotificationCloseOnOutside === false) return false;
    return true;
  }

  #overlayEnabled() {
    if (this.dataset.showOverlay === 'false') return false;
    if (window.Theme?.cartNotificationShowOverlay === false) return false;
    return true;
  }

  #dismissFromOutside(event) {
    if (!this.#closeOnOutsideEnabled() || !this.#shouldStayOpen) return;
    if (Date.now() < this.#ignoreOutsideUntil) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.close('outside');
  }

  #handleOutsidePointerDown(event) {
    if (!this.#closeOnOutsideEnabled() || !this.#shouldStayOpen) return;
    if (Date.now() < this.#ignoreOutsideUntil) return;

    const target = event.target;
    if (!(target instanceof Node)) return;
    if (this.notification?.contains(target)) return;

    this.#dismissFromOutside(event);
  }

  #isNotificationMode() {
    const cartType = window.Theme?.cartType || document.body?.dataset?.dcodedLlpCartMode;
    return (
      cartType === 'notification_drawer' ||
      cartType === 'notification' ||
      document.body?.classList.contains('dcoded-llp-notification-mode')
    );
  }

  #suppressForeignNotifications() {
    if (!this.#isNotificationMode()) return;

    const kill = (root) => {
      (root || document).querySelectorAll?.('cart-notification').forEach((el) => {
        if (el.tagName !== 'DCODED-LLP-CART-NOTIFICATION') el.remove();
      });
      (root || document).querySelectorAll?.('#cart-notification').forEach((el) => {
        if (el.id === 'cart-notification') el.remove();
      });
    };

    kill(document);
    this.#observer?.disconnect();
    this.#observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.tagName === 'CART-NOTIFICATION') node.remove();
          if (node.id === 'cart-notification') node.remove();
          // New inactive toast copy from header remorph — drop it, keep live
          if (
            node.id === 'dcoded-llp-cart-notification' &&
            node.getAttribute('data-dcoded-llp-live') !== 'true'
          ) {
            node.remove();
            continue;
          }
          kill(node);
        }
      }
    });
    this.#observer.observe(document.body, { childList: true, subtree: true });
  }

  #assertOpen() {
    if (!this.#shouldStayOpen || this.#asserting) return;
    this.#asserting = true;
    try {
      this.#mountPanel();
      if (!this.notification) return;

      this.notification.setAttribute('data-open', 'true');
      this.notification.setAttribute('data-dcoded-llp-live', 'true');
      this.notification.classList.add('animate', 'active');
      this.notification.classList.remove('closing', 'is-closing');
      this.#applyOpenStyles();
      this.#setScrimVisible(true);
    } finally {
      this.#asserting = false;
    }
  }

  #startOpenGuard() {
    if (!this.notification) return;

    this.#openGuard?.disconnect();
    this.#openGuard = new MutationObserver(() => {
      if (!this.#shouldStayOpen || this.#asserting) return;
      const open = this.notification?.getAttribute('data-open') === 'true';
      const active = this.notification?.classList.contains('active');
      if (!open || !active) this.#assertOpen();
    });

    this.#openGuard.observe(this.notification, {
      attributes: true,
      attributeFilter: ['class', 'style', 'data-open', 'hidden'],
    });

    if (!this.#heartbeat) {
      this.#heartbeat = window.setInterval(() => {
        if (!this.#shouldStayOpen) {
          this.#stopHeartbeat();
          return;
        }
        if (!this.notification || !document.body.contains(this.notification)) {
          this.#assertOpen();
        } else if (this.notification.getAttribute('data-open') !== 'true') {
          this.#assertOpen();
        }
      }, 250);
    }
  }

  #stopOpenGuard() {
    this.#openGuard?.disconnect();
    this.#openGuard = null;
    this.#stopHeartbeat();
  }

  #stopHeartbeat() {
    if (this.#heartbeat) {
      clearInterval(this.#heartbeat);
      this.#heartbeat = null;
    }
  }

  #applyOpenStyles() {
    if (!this.notification) return;
    const s = this.notification.style;
    s.setProperty('display', 'block', 'important');
    s.setProperty('opacity', '1', 'important');
    s.setProperty('visibility', 'visible', 'important');
    s.setProperty('pointer-events', 'auto', 'important');
    s.setProperty('transform', 'translate3d(0, 0, 0)', 'important');
    s.setProperty('z-index', '2147483000', 'important');
    s.setProperty('position', 'fixed', 'important');
    this.notification.hidden = false;
    this.notification.removeAttribute('hidden');
  }

  #setScrimVisible(visible) {
    if (!(this.scrim instanceof HTMLElement)) this.#mountScrim();
    if (!(this.scrim instanceof HTMLElement)) return;
    const show = Boolean(visible) && this.#overlayEnabled();
    this.scrim.classList.toggle('is-visible', show);
    if (show) {
      this.scrim.removeAttribute('hidden');
    } else {
      this.scrim.setAttribute('hidden', '');
    }
  }

  #clearOpenStyles() {
    if (!this.notification) return;
    const s = this.notification.style;
    ['display', 'opacity', 'visibility', 'pointer-events', 'transform', 'z-index', 'position'].forEach((prop) => {
      s.removeProperty(prop);
    });
  }

  /** @param {CustomEvent} event */
  handleAtcStart(event) {
    if (!this.#isNotificationMode()) return;
    if (Date.now() < this.#ignoreReopenUntil) return;

    this.#mountPanel();
    this.#bindPanelUi();
    this.#pendingAtcOpen = true;
    // Clear previous line so we never flash the last-added product.
    this.#clearProductRow();

    const detail = event.detail || {};
    if (detail.title || detail.product_title || detail.image) {
      this.#paintProduct(
        {
          product_title: detail.title || detail.product_title || '',
          title: detail.title || detail.product_title || '',
          image: detail.image || '',
          packs: detail.packs,
          isSubscription: detail.isSubscription,
          months: detail.months,
          sale: detail.sale || '',
          compare: detail.compare || '',
          perPack: detail.perPack || '',
          selling_plan_allocation: detail.sellingPlan
            ? { selling_plan: { name: detail.sellingPlan } }
            : null,
        },
        { allowPageFallback: false }
      );
    }

    // Open immediately once the new product is painted (don't wait for cart:update).
    if (this.#hasProductRow) {
      this.#openWhenProductReady();
    }
  }

  /** Wipe product slot + paint state (keeps panel mounted). */
  #clearProductRow() {
    const container = this.notification?.querySelector('#dcoded-llp-cart-notification-product');
    if (container) container.innerHTML = '';
    this.#hasProductRow = false;
    this.#paintedItem = null;
  }

  /**
   * Open only when the product row is actually painted.
   * @returns {boolean}
   */
  #openWhenProductReady() {
    if (!this.#hasProductRow) return false;
    this.#pendingAtcOpen = false;
    this.open();
    return true;
  }

  open() {
    this.#mountPanel();
    this.#bindPanelUi();
    if (!this.notification) return;

    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }

    this.#shouldStayOpen = true;
    window.__dcodedLlpNotificationShouldStayOpen = true;
    this.#ignoreOutsideUntil = Date.now() + 400;

    this.cacheCartTrigger();
    this.updatePosition();
    this.updateCartButton();
    this.notification.classList.remove('closing', 'is-closing');
    this.notification.classList.add('animate', 'active', 'is-opening');
    this.notification.setAttribute('data-open', 'true');
    this.#applyOpenStyles();
    this.#setScrimVisible(true);
    this.#startOpenGuard();

    document.dispatchEvent(
      new CustomEvent('dcoded-llp:notification-opened', {
        bubbles: true,
      })
    );

    // Re-assert after ATC button / cart-bubble / fly-to-cart animations finish
    [400, 800, 1200, 1800].forEach((ms) => {
      window.setTimeout(() => {
        if (!this.#shouldStayOpen) return;
        this.#assertOpen();
        this.updatePosition();
        this.updateCartButton();
      }, ms);
    });

    window.setTimeout(() => {
      this.notification?.classList.remove('is-opening');
    }, 450);
  }

  /**
   * @param {'button' | 'escape' | 'view-cart' | 'outside'} [reason]
   */
  close(reason) {
    if (!reason) return;
    if (!this.#shouldStayOpen && !this.notification?.hasAttribute('data-open')) return;

    this.#shouldStayOpen = false;
    window.__dcodedLlpNotificationShouldStayOpen = false;
    this.#pendingAtcOpen = false;
    this.#stopOpenGuard();
    if (reason === 'outside' || reason === 'button' || reason === 'escape') {
      this.#ignoreReopenUntil = Date.now() + 800;
    }

    if (!this.notification) return;

    this.notification.classList.add('closing', 'is-closing');
    this.notification.classList.remove('active', 'is-opening');
    this.notification.removeAttribute('data-open');
    this.#clearOpenStyles();
    this.#clearProductRow();

    if (reason === 'outside') {
      this.scrim?.classList.add('is-closing');
      window.setTimeout(() => {
        this.scrim?.classList.remove('is-closing');
        this.#setScrimVisible(false);
      }, 400);
    } else {
      this.#setScrimVisible(false);
    }

    this.closeTimeout = window.setTimeout(() => {
      this.notification?.classList.remove('closing', 'is-closing');
      this.closeTimeout = null;
    }, 400);
  }

  /**
   * @param {Record<string, any> | null | undefined} item
   * @param {{ allowPageFallback?: boolean }} [opts]
   */
  #paintProduct(item, opts = {}) {
    const container = this.notification?.querySelector('#dcoded-llp-cart-notification-product');
    if (!container) return;

    const allowPageFallback = opts.allowPageFallback !== false;
    const html =
      this.#renderProductFromItem(item) || (allowPageFallback ? this.#renderProductFromPage() : '');
    if (!html) return;

    container.innerHTML = html;
    this.#hasProductRow = true;
    const extras =
      item && typeof item === 'object' ? this.#normalizeExtras(item) : this.#readPageExtras();
    this.#paintedItem = {
      ...(item && typeof item === 'object' ? item : {}),
      ...extras,
    };
  }

  /**
   * @param {Record<string, any> | undefined} item
   * @returns {string}
   */
  #renderProductFromItem(item) {
    if (!item || typeof item !== 'object') return '';

    const title = item.product_title || item.title || '';
    const imageSrc = item.image || item.featured_image?.url || item.featured_image?.src || '';
    if (!title && !imageSrc) return '';

    const extras = this.#normalizeExtras(item);
    return this.#productMarkup(title, imageSrc, extras);
  }

  #renderProductFromPage() {
    const form = document.querySelector('product-form-component[data-dcoded-llp-product-title]');
    const title =
      form?.getAttribute('data-dcoded-llp-product-title') ||
      document.querySelector('.product-title-block .text-block')?.textContent?.trim() ||
      document.querySelector('.product-title-block')?.textContent?.trim() ||
      '';

    const image =
      form?.getAttribute('data-dcoded-llp-product-image') ||
      document.querySelector('dcoded-llp-media-gallery img, media-gallery img, .product-media-container img')
        ?.currentSrc ||
      document.querySelector('dcoded-llp-media-gallery img, media-gallery img')?.src ||
      '';

    if (!title && !image) return '';
    return this.#productMarkup(title, image, this.#readPageExtras());
  }

  /**
   * @param {Record<string, any>} item
   */
  #normalizeExtras(item) {
    const page = this.#readPageExtras();
    const packs = Number(item.packs) > 0 ? Number(item.packs) : page.packs;
    const isSubscription =
      typeof item.isSubscription === 'boolean'
        ? item.isSubscription
        : Boolean(item.selling_plan_allocation?.selling_plan?.name || page.isSubscription);
    const months = Number(item.months) > 0 ? Number(item.months) : page.months;
    const money = this.#lineItemMoney(item);
    return {
      packs,
      isSubscription,
      months,
      sale: item.sale || money.sale || page.sale,
      compare: item.compare || money.compare || page.compare,
      perPack: item.perPack || page.perPack,
    };
  }

  /**
   * @param {Record<string, any>} item
   */
  #lineItemMoney(item) {
    const qty = Number(item.quantity) > 0 ? Number(item.quantity) : 0;
    const saleCents =
      item.final_line_price ?? item.line_price ?? (typeof item.final_price === 'number' ? item.final_price * (qty || 1) : null);
    const compareCents = item.original_line_price ?? item.original_price ?? null;
    const sale = this.#formatCents(saleCents);
    const compare = this.#formatCents(compareCents);
    let perPack = '';
    const packs = Number(item.packs) > 0 ? Number(item.packs) : qty;
    if (sale && packs > 1) {
      const n = this.#moneyNum(sale);
      if (n) perPack = this.#formatNumber(n / packs);
    }
    return { sale, compare, perPack };
  }

  /** @param {number | string | null | undefined} cents */
  #formatCents(cents) {
    const n = Number(cents);
    if (!Number.isFinite(n) || n <= 0) return '';
    return this.#formatNumber(n / 100);
  }

  /** @param {number} amount */
  #formatNumber(amount) {
    if (!Number.isFinite(amount)) return '';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'GBP' }).format(amount);
    } catch {
      return `£${amount.toFixed(2)}`;
    }
  }

  /** @param {string} text */
  #moneyNum(text) {
    const cleaned = String(text).replace(/[^\d.,]/g, '');
    if (!cleaned) return 0;
    return Number.parseFloat(cleaned.replace(/,/g, '')) || 0;
  }

  #readPageExtras() {
    const packs = this.#readPackCount();
    const isSubscription = this.#readIsSubscription();
    const months = this.#readMonths(packs);
    const prices = this.#readKachingPrices();
    let perPack = prices.perPack || '';
    if (!perPack && prices.sale && packs > 1) {
      const n = this.#moneyNum(prices.sale);
      if (n) perPack = this.#formatNumber(n / packs);
    }
    return {
      packs,
      isSubscription,
      months,
      sale: prices.sale,
      compare: prices.compare,
      perPack,
    };
  }

  #readPackCount() {
    let packs = 0;
    document.querySelectorAll('[class*="kaching-bundles__bar"][class*="selected"], .kaching-bundles__bar-container--selected').forEach((el) => {
      if (packs || !(el instanceof HTMLElement)) return;
      const text = (el.textContent || '').replace(/\s+/g, ' ');
      if (/subscribe|one[-\s]?time/i.test(text) && !/buy\s+\d+\s+packs?/i.test(text)) return;
      const match = text.match(/Buy\s+(\d+)\s+Packs?/i);
      if (match) packs = Number.parseInt(match[1], 10) || 0;
    });
    if (packs > 0) return packs;

    const qty =
      document.querySelector('form[action*="/cart/add"] input[name="quantity"], quantity-input input, .quantity__input');
    if (qty instanceof HTMLInputElement) {
      const n = Number.parseInt(qty.value, 10);
      if (n > 0) return n;
    }
    return 1;
  }

  #readIsSubscription() {
    const checked =
      document.querySelector('input[name="selling_plan"]:checked') ||
      document.querySelector('input[name="selling_plan"][value]:not([value=""])');
    if (checked instanceof HTMLInputElement && checked.value) return true;
    const freq = document.querySelector(
      '[class*="subscriptions_card"][class*="selected"], [class*="ks-card"][class*="selected"]'
    );
    if (freq && /subscribe/i.test(freq.textContent || '')) return true;
    return false;
  }

  /** @param {number} packs */
  #readMonths(packs) {
    const supply = document.querySelector('[data-dcoded-bundle-supply]');
    const supplyText = (supply?.textContent || '').replace(/\s+/g, ' ');
    const supplyMatch = supplyText.match(/(\d+)\s*[-–—\s]*Month/i);
    if (supplyMatch) return Number.parseInt(supplyMatch[1], 10) || 0;

    const freq = document.querySelector(
      '[class*="subscriptions_card"][class*="selected"], [class*="ks-card"][class*="selected"]'
    );
    const freqText = (freq?.textContent || '').replace(/\s+/g, ' ');
    const every = freqText.match(/every\s+(\d+)\s+months?/i);
    if (every) return Number.parseInt(every[1], 10) || 0;

    return packs > 0 ? packs * 3 : 0;
  }

  #readKachingPrices() {
    /** @type {HTMLElement | null} */
    let card = null;
    document.querySelectorAll('[class*="subscriptions_card"], [class*="ks-card"], [class*="onetime"], [class*="one-time"]').forEach((el) => {
      if (card || !(el instanceof HTMLElement)) return;
      if (el.querySelector('input[type="radio"]:checked') || /selected/i.test(el.className)) {
        card = el;
      }
    });
    if (!card) {
      document.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
        if (card || !(input instanceof HTMLInputElement)) return;
        if (!input.closest('[class*="kaching"], [class*="ks-"]')) return;
        const wrap = input.closest('[class*="subscriptions_card"], [class*="ks-card"], [class*="kaching-bundles__bar-container"]');
        if (wrap instanceof HTMLElement && !/buy\s+\d+\s+packs?/i.test(wrap.textContent || '')) {
          card = wrap;
        }
      });
    }
    if (!card) return { sale: '', compare: '', perPack: '' };

    const compareEl = card.querySelector('s, del, [class*="compare"], [class*="original"]');
    const saleEl = card.querySelector(
      '.kaching-bundles__bar-price, .ks-discounted-price, [data-testid="discount-price"], [data-testid="discounted-price"]'
    );
    const parse = (text) => {
      const match = String(text || '').match(/[£$€]\s*[\d,.]+/);
      return match ? match[0].replace(/\s+/g, '') : '';
    };
    const all = [...(card.textContent || '').matchAll(/[£$€]\s*[\d,.]+(?!\s*per\s*pack)/gi)].map((m) =>
      m[0].replace(/\s+/g, '')
    );
    const unique = [...new Set(all)];
    let sale = parse(saleEl?.textContent) || '';
    let compare = parse(compareEl?.textContent) || '';
    if (!sale && unique.length) {
      unique.sort((a, b) => this.#moneyNum(a) - this.#moneyNum(b));
      sale = unique[0];
      compare = compare || unique[unique.length - 1];
    }
    if (compare && this.#moneyNum(compare) <= this.#moneyNum(sale)) compare = '';
    const perMatch = (card.textContent || '').match(/[£$€]\s*[\d,.]+\s*\/?\s*per\s*pack/i);
    const perPack = perMatch ? parse(perMatch[0]) : '';
    return { sale, compare, perPack };
  }

  /**
   * @param {string} title
   * @param {string} imageSrc
   * @param {{packs?: number, isSubscription?: boolean, months?: number, sale?: string, compare?: string, perPack?: string}} extras
   */
  #productMarkup(title, imageSrc, extras = {}) {
    const safeTitle = this.#escapeHtml(title);
    const packs = Number(extras.packs) > 0 ? Number(extras.packs) : 1;
    const packLabel = `${packs}-Pack ${extras.isSubscription ? 'Subscription' : 'One-time'}`;
    const monthLabel = Number(extras.months) > 0 ? `${Number(extras.months)}-Month Supply` : '';
    const meta = monthLabel ? `${packLabel} · ${monthLabel}` : packLabel;
    const sale = extras.sale || '';
    const compare = extras.compare && extras.compare !== sale ? extras.compare : '';
    let perPack = extras.perPack || '';
    if (!perPack && sale && packs > 1) {
      const n = this.#moneyNum(sale);
      if (n) perPack = this.#formatNumber(n / packs);
    }

    const image = imageSrc
      ? `<div class="dcoded-llp-cart-notification-product__image">
          <img src="${this.#escapeAttr(imageSrc)}" alt="" loading="lazy" width="60" height="74">
        </div>`
      : '';

    const prices =
      sale || compare || perPack
        ? `<div class="dcoded-llp-cart-notification-product__prices">
            ${sale ? `<span class="dcoded-llp-cart-notification-product__now">${this.#escapeHtml(sale)}</span>` : ''}
            ${compare ? `<span class="dcoded-llp-cart-notification-product__was">${this.#escapeHtml(compare)}</span>` : ''}
            ${perPack ? `<span class="dcoded-llp-cart-notification-product__per">· ${this.#escapeHtml(perPack)} / pack</span>` : ''}
          </div>`
        : '';

    return `
      ${image}
      <div class="dcoded-llp-cart-notification-product__info">
        <h3 class="dcoded-llp-cart-notification-product__name">${safeTitle}</h3>
        <p class="dcoded-llp-cart-notification-product__meta">${this.#escapeHtml(meta)}</p>
        ${prices}
      </div>
    `;
  }

  /** @param {string} value */
  #escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /** @param {string} value */
  #escapeAttr(value) {
    return this.#escapeHtml(value).replace(/'/g, '&#39;');
  }

  #preferredDisplayTitle(resource) {
    const painted = (this.#paintedItem?.product_title || this.#paintedItem?.title || '')
      .toString()
      .trim();
    if (painted) return painted;

    const pageTitle =
      document
        .querySelector('product-form-component[data-dcoded-llp-product-title]')
        ?.getAttribute('data-dcoded-llp-product-title') ||
      document.querySelector('.product-title-block .text-block')?.textContent?.trim() ||
      document.querySelector('.product-title-block')?.textContent?.trim() ||
      '';
    if (pageTitle) return pageTitle;

    return (resource?.product_title || resource?.title || '').toString().trim();
  }

  handleCartUpdate(event) {
    if (!this.#isNotificationMode()) return;

    const data = event.detail?.data;
    if (data?.source !== 'product-form-component' || data?.redirectToCheckout) return;
    if (data?.skipCartNotification) return;

    this.updateCartButton();
    this.updatePosition();

    if (data?.didError) {
      this.#pendingAtcOpen = false;
      return;
    }

    if (Date.now() < this.#ignoreReopenUntil) return;

    const resource = event.detail?.resource;
    if (resource && (resource.product_title || resource.title || resource.image)) {
      const preferredTitle = this.#preferredDisplayTitle(resource);
      // Keep trimmed PDP title — never replace with Shopify's full product.title
      if (this.#hasProductRow && preferredTitle) {
        this.#mergeLineItemPrices({
          ...resource,
          product_title: preferredTitle,
          title: preferredTitle,
        });
      } else {
        this.#paintProduct(
          {
            ...resource,
            product_title: preferredTitle,
            title: preferredTitle,
          },
          { allowPageFallback: false }
        );
      }
    } else if (this.#hasProductRow && resource) {
      this.#mergeLineItemPrices(resource);
    }

    // Fallback open if snapshot was missing at ATC start.
    if (this.#pendingAtcOpen) {
      this.#openWhenProductReady();
    } else if (this.#shouldStayOpen) {
      this.updateCartButton();
    }
  }

  /**
   * Fill missing snapshot prices from the cart line item without replacing a complete row.
   * @param {Record<string, any>} resource
   */
  #mergeLineItemPrices(resource) {
    const painted = this.#paintedItem || {};
    if (painted.sale && painted.compare) return;

    const packs = Number(painted.packs) > 0 ? Number(painted.packs) : Number(resource.quantity) || 1;
    const money = this.#lineItemMoney({ ...resource, packs });
    if (!money.sale && !money.compare) return;

    this.#paintProduct({
      ...painted,
      product_title: painted.product_title || resource.product_title || resource.title || '',
      image: painted.image || resource.image || '',
      packs,
      isSubscription:
        typeof painted.isSubscription === 'boolean'
          ? painted.isSubscription
          : Boolean(resource.selling_plan_allocation?.selling_plan),
      months: painted.months,
      sale: painted.sale || money.sale,
      compare: painted.compare || money.compare,
      perPack: painted.perPack || money.perPack,
    });
  }

  handleViewCartClick(event) {
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return;
    if (!button.hasAttribute('data-open-cart-drawer') && window.Theme?.cartType !== 'notification_drawer') return;

    event.preventDefault();
    event.stopPropagation();
    this.close('view-cart');

    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  }

  updateCartButton() {
    const cartButton = this.notification?.querySelector('#dcoded-llp-cart-notification-button');
    if (!cartButton) return;

    const cartBubbleCount =
      document.querySelector('[ref="cartBubbleCount"]') ||
      document.querySelector('.cart-bubble__text') ||
      document.querySelector('[data-testid="cart-bubble"]');
    const currentCount = Number.parseInt(cartBubbleCount?.textContent?.replace(/\D/g, '') || '0', 10);
    const cartCount = Number.isNaN(currentCount) ? 0 : currentCount;
    const label = window.Theme?.translations?.view_cart || 'View cart';

    cartButton.textContent = cartCount > 0 ? `${label} (${cartCount})` : label;
  }

  cacheCartTrigger() {
    this.cartTrigger =
      document.querySelector('[data-testid="cart-drawer-trigger"]') ||
      document.querySelector('.action__cart') ||
      document.querySelector('.header-actions__cart-icon')?.closest('a, button');
  }

  updatePosition() {
    if (!this.notification) return;

    if (window.innerWidth <= 640) {
      this.notification.style.removeProperty('--dcoded-llp-notification-top');
      this.notification.style.removeProperty('--dcoded-llp-notification-right');
      this.notification.style.setProperty('top', 'auto', 'important');
      this.notification.style.setProperty('bottom', '0', 'important');
      this.notification.style.setProperty('left', '0', 'important');
      this.notification.style.setProperty('right', '0', 'important');
      this.notification.style.setProperty('width', '100%', 'important');
      this.notification.style.setProperty('max-width', '100%', 'important');
      return;
    }

    this.notification.style.removeProperty('left');
    this.notification.style.removeProperty('bottom');
    this.notification.style.removeProperty('width');
    this.notification.style.removeProperty('max-width');
    this.notification.style.removeProperty('top');

    if (!this.cartTrigger || !document.body.contains(this.cartTrigger)) {
      this.cacheCartTrigger();
    }

    const trigger = this.cartTrigger || document.querySelector('.header-actions__cart-icon');
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const right = Math.max(24, window.innerWidth - rect.right);
    const top = rect.bottom + 12;

    this.notification.style.setProperty('--dcoded-llp-notification-top', `${top}px`);
    this.notification.style.setProperty('--dcoded-llp-notification-right', `${right}px`);
  }
}

if (!customElements.get('dcoded-llp-cart-notification')) {
  customElements.define('dcoded-llp-cart-notification', DcodedLlpCartNotification);
}
